import CanvasJSReact from '@canvasjs/react-charts'
import {
  CHART_DEFAULT_OPTIONS,
  LABEL_FONT_SIZE,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UnitConversions,
  UserUnitSettings,
} from '../../common/constants'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { convertBaseToDisplay } from '../../common/conversions'
import { DeviceInfo, UnitCategoryId } from '../../common/enums'
import { Viewport } from '../../common/types'

const { CanvasJSChart } = CanvasJSReact

const CHART_TYPE_LINE = 'line'

interface Props {
  device: DeviceInfo
  chartName: string
  isLoading: boolean
  batteryReadings: Record<string, number>
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
  zoomEnabled?: boolean
}

export function BatteryLineChart({
  device,
  isLoading,
  chartName,
  batteryReadings,
  viewport,
  userUnitSettings,
  unitConversions,
  zoomEnabled = true,
}: Props) {
  const timestamps = batteryReadings ? Object.keys(batteryReadings) : []

  const filterMinimumValue: number | undefined = undefined
  const filterMaximumValue: number | undefined = undefined

  const unitCategoryId = UnitCategoryId.ElectricPotentialDifference
  const unitId = userUnitSettings[unitCategoryId].unitId
  const units = unitConversions[unitCategoryId as UnitCategoryId].abbreviations[unitId]
  const formula = unitConversions[unitCategoryId as UnitCategoryId].formulas[unitId]
  const precision = userUnitSettings[unitCategoryId].precision

  const minTimestamp = timestamps.length > 0 ? parseInt(timestamps[0]) * 1000 : viewport.minimum
  const maxTimestamp = timestamps.length > 0 ? parseInt(timestamps[timestamps.length - 1]) * 1000 : viewport.maximum
  const minX = viewport.minimum > minTimestamp ? viewport.minimum : minTimestamp
  const maxX = viewport.maximum < maxTimestamp ? viewport.maximum : maxTimestamp

  const readings = batteryReadings
  let trackDay = -1

  const dataPoints = timestamps
    .map((key: string) => {
      const ts = parseInt(key)

      const reading = convertBaseToDisplay(Number(readings[ts]), formula)

      return {
        x: ts * 1000,
        y: roundToDecimal(reading, precision),
      }
    })
    .filter((dataPoint: any) => (filterMinimumValue !== undefined ? dataPoint.y > filterMinimumValue : true))
    .filter((dataPoint: any) => (filterMaximumValue !== undefined ? dataPoint.y < filterMaximumValue : true))

  const isThereAnyDataToRender =
    dataPoints.filter((dataPoint: any) => dataPoint.x > minX).filter((dataPoint: any) => dataPoint.x < maxX).length > 0

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_${chartName}`,
    title: {
      text: chartName,
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`
        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y, precision)} ${units} </strong><br/>`
        }
        return content
      },
    },
    axisY: {
      includeZero: false,
      title: `${chartName} (${units})`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: LABEL_FONT_SIZE,
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: minX || null,
      viewportMaximum: maxX || null,
    },
    data: [
      {
        name: chartName,
        type: CHART_TYPE_LINE,
        showInLegend: true,
        xValueType: 'dateTime',
        dataPoints,
      },
    ],
  }

  function getLastReadingTimestamp(): number {
    const timestamps = batteryReadings ? Object.keys(batteryReadings).map(Number) : []
    timestamps.sort((a, b) => Number(a) - Number(b))

    return timestamps.length > 0 ? timestamps[timestamps.length - 1] : 0
  }

  const data: string[][] = [
    [
      chartName,
      `${getLastReadingTimestamp() === 0 ? 'No readings' : getLastReadingTimestamp()}`,
      `${roundToDecimal(dataPoints[dataPoints.length - 1]?.y || 0, precision)} ${units}`,
    ],
  ]

  return (
    <CollapsibleSection title={chartName} open={true} isLoading={isLoading}>
      <ChartSubHeader data={data} />
      {isThereAnyDataToRender ? (
        <CanvasJSChart options={options} />
      ) : (
        <div className={tailwind.noReadingsMessage}>This RIC has no data for the selected period</div>
      )}
    </CollapsibleSection>
  )
}

const tailwind = {
  noReadingsMessage:
    'bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h-10 w-[320px] h-[70px] mx-auto text-center',
}
